import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { usePageMeta } from '@/hooks/usePageMeta';

const PrivacyPage = () => {
  usePageMeta({
    title: 'Privacy Policy | Talent Collective',
    description: 'Learn how Talent Collective collects, uses, and protects your personal information.'
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <Header />

      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container-custom">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: February 2026</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom max-w-3xl">
          <Card className="p-8 rounded-2xl border-gray-100">
            <div className="prose prose-stone max-w-none">
              <h2 className="text-xl font-bold text-stone-800 mb-4">1. Introduction</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                Talent Collective ("we", "our", "the Platform") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, and safeguard your information when you use our Platform.
                The Platform is operated by ACT Capital Foundation For Social Impact.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">2. Information We Collect</h2>
              <p className="text-stone-600 mb-4 leading-relaxed">
                We collect information you provide directly:
              </p>
              <ul className="list-disc pl-6 text-stone-600 mb-6 space-y-2">
                <li>Account information (name, email, password)</li>
                <li>Profile information (skills, experience, resume)</li>
                <li>Application data (cover letters, responses)</li>
                <li>Communication preferences</li>
              </ul>

              <h2 className="text-xl font-bold text-stone-800 mb-4">3. How We Use Your Information</h2>
              <p className="text-stone-600 mb-4 leading-relaxed">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 text-stone-600 mb-6 space-y-2">
                <li>Operate and improve the Platform</li>
                <li>Match you with relevant opportunities</li>
                <li>Facilitate connections with organizations</li>
                <li>Send relevant notifications and updates</li>
                <li>Ensure platform security and prevent fraud</li>
              </ul>

              <h2 className="text-xl font-bold text-stone-800 mb-4">4. Information Sharing</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                We share your information only with:
              </p>
              <ul className="list-disc pl-6 text-stone-600 mb-6 space-y-2">
                <li>Organizations you apply to (profile and application data)</li>
                <li>Service providers who help operate the Platform</li>
                <li>Legal authorities when required by law</li>
              </ul>
              <p className="text-stone-600 mb-6 leading-relaxed">
                We never sell your personal information to third parties.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">5. Data Security</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                We implement appropriate security measures to protect your information, including 
                encryption, secure servers, and access controls. However, no method of transmission 
                over the internet is 100% secure.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">6. Your Rights</h2>
              <p className="text-stone-600 mb-4 leading-relaxed">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-stone-600 mb-6 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Export your data in a portable format</li>
                <li>Opt out of marketing communications</li>
              </ul>

              <h2 className="text-xl font-bold text-stone-800 mb-4">7. Data Retention</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                We retain your information as long as your account is active or as needed to provide services. 
                You can request deletion of your account and associated data at any time.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">8. Cookies</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                We use essential cookies to operate the Platform and analytics cookies to understand usage. 
                You can control cookie preferences through your browser settings.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">9. Changes to This Policy</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                We may update this Privacy Policy periodically. We will notify you of significant changes 
                via email or through the Platform.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">10. Contact Us</h2>
              <p className="text-stone-600 leading-relaxed">
                For privacy-related questions or requests, contact us at{' '}
                <a href="mailto:privacy@actgrants.in" className="text-teal-600 hover:underline">
                  privacy@actgrants.in
                </a>
              </p>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
